import { Tabs } from 'antd';
import { ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { sectionTrail } from '../../../shared/components/section-trail';
import { DocumentTypesPanel } from '../features/document-types/components/DocumentTypesPanel';
import { EscalationLevelsPanel } from '../features/escalation-levels/components/EscalationLevelsPanel';
import { ContainerSizesPanel } from '../features/container-sizes/components/ContainerSizesPanel';
import { ContainerTypesPanel } from '../features/container-types/components/ContainerTypesPanel';
import { WeightBandsPanel } from '../features/weight-bands/components/WeightBandsPanel';
import { CARGO_CATALOGUE_TABS, REFERENCE_DATA_TAB_LABELS as LABELS, referenceDataTabPath, type ReferenceDataTab } from '../reference-data.paths';
import { JobTypesPanel } from '../features/job-types/components/JobTypesPanel';
import { TrailerTypesPanel } from '../features/trailer-types/components/TrailerTypesPanel';
import { TruckClassesPanel } from '../features/truck-classes/components/TruckClassesPanel';
import { useDocumentTypes } from '../features/document-types/hooks/use-document-types';
import { useEscalationLevels } from '../features/escalation-levels/hooks/use-escalation-levels';
import { useJobTypes } from '../features/job-types/hooks/use-job-types';
import { ReasonCodesPanel } from '../features/reason-codes/components/ReasonCodesPanel';
import { useJobReasonCodes } from '../features/reason-codes/hooks/use-job-reason-codes';
import { useRequiredDocumentTypes } from '../features/required-document-types/hooks/use-required-document-types';
import { useTrailerTypes } from '../features/trailer-types/hooks/use-trailer-types';
import { useTruckClasses } from '../features/truck-classes/hooks/use-truck-classes';
import { useZoneKinds } from '../features/zone-kinds/public/use-zone-kinds';
import { ZoneKindsPanel } from '../features/zone-kinds/components/ZoneKindsPanel';

function TruckClassesTab({ hook }: { hook: ReturnType<typeof useTruckClasses> }) {
  return (
    <TruckClassesPanel
      classes={hook.state.status === 'loaded' ? hook.state.classes : []}
      loading={hook.state.status === 'loading'}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
    />
  );
}

function TrailerTypesTab({ hook }: { hook: ReturnType<typeof useTrailerTypes> }) {
  return (
    <TrailerTypesPanel
      types={hook.state.status === 'loaded' ? hook.state.types : []}
      loading={hook.state.status === 'loading'}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
    />
  );
}

function DocumentTypesTab({ hook }: { hook: ReturnType<typeof useDocumentTypes> }) {
  return (
    <DocumentTypesPanel
      types={hook.state.status === 'loaded' ? hook.state.types : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function JobTypesTab({
  jobTypes,
  requiredDocumentTypes,
}: {
  jobTypes: ReturnType<typeof useJobTypes>;
  requiredDocumentTypes: ReturnType<typeof useRequiredDocumentTypes>;
}) {
  return (
    <JobTypesPanel
      types={jobTypes.state.status === 'loaded' ? jobTypes.state.types : []}
      loading={jobTypes.state.status === 'loading'}
      saveError={jobTypes.saveError}
      saving={jobTypes.saving}
      onCreate={jobTypes.create}
      onUpdate={jobTypes.update}
      onDeactivate={jobTypes.deactivate}
      onReactivate={jobTypes.reactivate}
      onResetSaveError={jobTypes.resetSaveError}
      requiredDocumentTypePanel={{
        types: requiredDocumentTypes.state.status === 'loaded' ? requiredDocumentTypes.state.types : [],
        loading: requiredDocumentTypes.state.status === 'loading',
        saveError: requiredDocumentTypes.saveError,
        saving: requiredDocumentTypes.saving,
        onCreate: requiredDocumentTypes.create,
        onUpdate: requiredDocumentTypes.update,
        onDeactivate: requiredDocumentTypes.deactivate,
        onReactivate: requiredDocumentTypes.reactivate,
        onResetSaveError: requiredDocumentTypes.resetSaveError,
      }}
    />
  );
}

function ReasonCodesTab({ hook }: { hook: ReturnType<typeof useJobReasonCodes> }) {
  return (
    <ReasonCodesPanel
      codes={hook.state.status === 'loaded' ? hook.state.codes : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function ZoneKindsTab({ hook }: { hook: ReturnType<typeof useZoneKinds> }) {
  return (
    <ZoneKindsPanel
      kinds={hook.state.status === 'loaded' ? hook.state.kinds : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function EscalationLevelsTab({ hook }: { hook: ReturnType<typeof useEscalationLevels> }) {
  return (
    <EscalationLevelsPanel
      levels={hook.state.status === 'loaded' ? hook.state.levels : []}
      loading={hook.state.status === 'loading'}
      saveError={hook.saveError}
      saving={hook.saving}
      onCreate={hook.create}
      onUpdate={hook.update}
      onDeactivate={hook.deactivate}
      onReactivate={hook.reactivate}
      onResetSaveError={hook.resetSaveError}
    />
  );
}

function cargoCatalogueTabs() {
  return [
    { key: 'container-sizes', label: LABELS['container-sizes'], children: <ContainerSizesPanel /> },
    { key: 'container-types', label: LABELS['container-types'], children: <ContainerTypesPanel /> },
    { key: 'weight-bands', label: LABELS['weight-bands'], children: <WeightBandsPanel /> },
  ];
}

/**
 * Fleet and pricing vocabulary: truck classes, trailer types, document types,
 * job types, container sizes, types and weight bands, zone kinds and the empty-return escalation levels, in one tabbed screen — the shape later
 * reference-data milestones (check templates, service intervals) land in
 * too, rather than growing the Admin nav by one entry each. Zones themselves
 * are places, not vocabulary, and have their own Operations section.
 */
export function ReferenceDataPage({ tab }: { tab: ReferenceDataTab }) {
  const truckClasses = useTruckClasses();
  const trailerTypes = useTrailerTypes();
  const documentTypes = useDocumentTypes();
  const jobTypes = useJobTypes();
  const requiredDocumentTypes = useRequiredDocumentTypes();
  const reasonCodes = useJobReasonCodes();
  const zoneKinds = useZoneKinds();
  const escalationLevels = useEscalationLevels();
  const navigate = useNavigate();
  const { state: session } = useCurrentUser();
  const canManageCargoCatalogues = session.status === 'authenticated' && session.user.permissions.includes('configuration.manage');
  const states = [
    truckClasses.state,
    trailerTypes.state,
    documentTypes.state,
    jobTypes.state,
    requiredDocumentTypes.state,
    reasonCodes.state,
    zoneKinds.state,
    escalationLevels.state,
  ];

  if (states.some((s) => s.status === 'forbidden')) return <Navigate to="/" replace />;
  if (!canManageCargoCatalogues && CARGO_CATALOGUE_TABS.includes(tab)) return <Navigate to={referenceDataTabPath('truck-classes')} replace />;

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('reference-data')}
        title="Reference data"
        description="The fleet and pricing vocabulary trucks, trailers, documents, job types and zones are described with."
      />
      {states.some((s) => s.status === 'error') ? (
        <EmptyState
          icon={ShieldAlert}
          title="Couldn't load reference data"
          description="Something went wrong loading the reference-data catalogues. Try reloading the page."
        />
      ) : (
        <Tabs
          activeKey={tab}
          onChange={(key) => navigate(referenceDataTabPath(key as ReferenceDataTab))}
          items={[
            { key: 'truck-classes', label: LABELS['truck-classes'], children: <TruckClassesTab hook={truckClasses} /> },
            { key: 'trailer-types', label: LABELS['trailer-types'], children: <TrailerTypesTab hook={trailerTypes} /> },
            { key: 'document-types', label: LABELS['document-types'], children: <DocumentTypesTab hook={documentTypes} /> },
            { key: 'job-types', label: LABELS['job-types'], children: <JobTypesTab jobTypes={jobTypes} requiredDocumentTypes={requiredDocumentTypes} /> },
            ...(canManageCargoCatalogues ? cargoCatalogueTabs() : []),
            { key: 'reason-codes', label: LABELS['reason-codes'], children: <ReasonCodesTab hook={reasonCodes} /> },
            { key: 'zone-kinds', label: LABELS['zone-kinds'], children: <ZoneKindsTab hook={zoneKinds} /> },
            { key: 'escalation-levels', label: LABELS['escalation-levels'], children: <EscalationLevelsTab hook={escalationLevels} /> },
          ]}
        />
      )}
    </>
  );
}
